import React, { useCallback, useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useStores } from '@/stores';
import { config } from '@/lib/config';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ClipboardList, Plus, Pencil, Trash2, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import type { Survey } from '@core/models';

interface QuestionFormRow {
  id: string;
  label: string;
  type: string;
  options: string; // comma separated in the form
}

interface SurveyFormState {
  title: string;
  description: string;
  questions: QuestionFormRow[];
}

const emptyForm = (): SurveyFormState => ({
  title: '',
  description: '',
  questions: [{ id: '', label: '', type: 'text', options: '' }],
});

const SurveyList: React.FC = observer(() => {
  const { tenantStore } = useStores();
  const [surveys, setSurveys] = useState<Survey[]>([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState('');
  const [form, setForm] = useState<SurveyFormState>(emptyForm());
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState('');

  const tenantId = tenantStore.selectedTenant?.id;

  const fetchSurveys = useCallback(async () => {
    if (!tenantId) return;

    setLoading(true);
    try {
      const response = await fetch(
        `${config.apiBaseUrl}/v1/tenants/${tenantId}/surveys`,
        {
          credentials: 'include',
        }
      );

      if (response.ok) {
        setSurveys((await response.json()) || []);
      } else {
        toast.error('アンケートの取得に失敗しました');
      }
    } catch (error) {
      console.error('Failed to fetch surveys:', error);
      toast.error('アンケートの取得に失敗しました');
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    fetchSurveys();
  }, [fetchSurveys]);

  const openCreateDialog = () => {
    setEditingId('');
    setForm(emptyForm());
    setDialogOpen(true);
  };

  const openEditDialog = (survey: Survey) => {
    setEditingId(survey.id);
    setForm({
      title: survey.title,
      description: survey.description,
      questions: survey.questions.map((q) => ({
        id: q.id,
        label: q.label,
        type: q.type,
        options: (q.options || []).join(','),
      })),
    });
    setDialogOpen(true);
  };

  const addQuestionRow = () => {
    setForm((prev) => ({
      ...prev,
      questions: [
        ...prev.questions,
        { id: '', label: '', type: 'text', options: '' },
      ],
    }));
  };

  const updateQuestionRow = (index: number, patch: Partial<QuestionFormRow>) => {
    setForm((prev) => ({
      ...prev,
      questions: prev.questions.map((row, i) => (i === index ? { ...row, ...patch } : row)),
    }));
  };

  const removeQuestionRow = (index: number) => {
    setForm((prev) => ({
      ...prev,
      questions: prev.questions.filter((_, i) => i !== index),
    }));
  };

  const validateForm = (): boolean => {
    if (!form.title.trim()) {
      toast.error('タイトルは必須です');
      return false;
    }
    if (form.questions.length === 0) {
      toast.error('質問を1つ以上追加してください');
      return false;
    }
    for (const q of form.questions) {
      if (!q.label.trim()) {
        toast.error('すべての質問にラベルを入力してください');
        return false;
      }
      if ((q.type === 'select' || q.type === 'multiselect') && !q.options.trim()) {
        toast.error('選択肢はカンマ区切りで入力してください');
        return false;
      }
    }
    return true;
  };

  const buildQuestionsPayload = () =>
    form.questions.map((q) => ({
      id: q.id || undefined,
      label: q.label.trim(),
      type: q.type,
      options:
        q.type === 'select' || q.type === 'multiselect'
          ? q.options.split(',').map((opt) => opt.trim()).filter(Boolean)
          : [],
    }));

  const handleSave = async () => {
    if (!tenantId || !validateForm()) return;

    const payload = {
      title: form.title.trim(),
      description: form.description.trim(),
      questions: buildQuestionsPayload(),
    };

    setSaving(true);
    try {
      const url = editingId
        ? `${config.apiBaseUrl}/v1/tenants/${tenantId}/surveys/${editingId}`
        : `${config.apiBaseUrl}/v1/tenants/${tenantId}/surveys`;

      const response = await fetch(url, {
        method: editingId ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        toast.error(error.error || (editingId ? '更新に失敗しました' : '作成に失敗しました'));
        return;
      }

      toast.success(editingId ? 'アンケートを更新しました' : 'アンケートを作成しました');
      setDialogOpen(false);
      await fetchSurveys();
    } catch (error) {
      console.error('Failed to save survey:', error);
      toast.error('保存に失敗しました');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (survey: Survey) => {
    if (!tenantId) return;

    try {
      const response = await fetch(
        `${config.apiBaseUrl}/v1/tenants/${tenantId}/surveys/${survey.id}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify({
            title: survey.title,
            description: survey.description,
            questions: survey.questions.map((q) => ({
              id: q.id,
              label: q.label,
              type: q.type,
              options: q.options || [],
            })),
            is_active: !survey.is_active,
          }),
        }
      );

      if (!response.ok) {
        toast.error('ステータスの変更に失敗しました');
        return;
      }

      toast.success(survey.is_active ? '無効にしました' : '有効にしました');
      await fetchSurveys();
    } catch (error) {
      console.error('Failed to toggle survey:', error);
      toast.error('ステータスの変更に失敗しました');
    }
  };

  const handleDelete = async (survey: Survey) => {
    if (!tenantId) return;
    if (!window.confirm(`「${survey.title}」を削除しますか?`)) return;

    setDeletingId(survey.id);
    try {
      const response = await fetch(
        `${config.apiBaseUrl}/v1/tenants/${tenantId}/surveys/${survey.id}`,
        {
          method: 'DELETE',
          credentials: 'include',
        }
      );

      if (!response.ok && response.status !== 204) {
        toast.error('削除に失敗しました');
        return;
      }

      toast.success('アンケートを削除しました');
      await fetchSurveys();
    } catch (error) {
      console.error('Failed to delete survey:', error);
      toast.error('削除に失敗しました');
    } finally {
      setDeletingId('');
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    return new Intl.DateTimeFormat('ja-JP', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(new Date(dateString));
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <CardTitle className="flex items-center gap-2">
            <ClipboardList className="h-5 w-5" />
            アンケート管理
          </CardTitle>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={fetchSurveys} disabled={loading}>
              <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
              更新
            </Button>
            <Button size="sm" onClick={openCreateDialog}>
              <Plus className="h-4 w-4 mr-1" />
              新規作成
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground mb-4">
            アンケートの回答はゲストの属性として保存され、セグメント配信の絞り込みに利用できます。
            LINE の通知設定画面 (LIFF) から回答を収集できます。
          </p>

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>タイトル</TableHead>
                <TableHead>説明</TableHead>
                <TableHead>質問数</TableHead>
                <TableHead>ステータス</TableHead>
                <TableHead>作成日</TableHead>
                <TableHead className="text-right">操作</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {surveys.map((survey) => (
                <TableRow key={survey.id}>
                  <TableCell className="font-medium">{survey.title}</TableCell>
                  <TableCell className="max-w-[240px] truncate text-muted-foreground">
                    {survey.description || '-'}
                  </TableCell>
                  <TableCell>{survey.questions.length}</TableCell>
                  <TableCell>
                    <Badge
                      variant={survey.is_active ? 'default' : 'secondary'}
                      className={survey.is_active ? 'bg-green-100 text-green-800 hover:bg-green-100' : ''}
                    >
                      {survey.is_active ? '有効' : '無効'}
                    </Badge>
                  </TableCell>
                  <TableCell>{formatDate(survey.created)}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button variant="ghost" size="icon" onClick={() => openEditDialog(survey)} aria-label="編集">
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleToggleActive(survey)}
                      >
                        {survey.is_active ? '無効化' : '有効化'}
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDelete(survey)}
                        disabled={deletingId === survey.id}
                        aria-label="削除"
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {surveys.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                    アンケートがありません。「新規作成」から追加してください。
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-auto">
          <DialogHeader>
            <DialogTitle>{editingId ? 'アンケート編集' : 'アンケート新規作成'}</DialogTitle>
            <DialogDescription>
              質問タイプが「選択」「複数選択」の場合は、選択肢をカンマ区切りで入力してください。
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-1">
              <Label htmlFor="survey-title">タイトル *</Label>
              <Input
                id="survey-title"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="例: ご利用状況アンケート"
              />
            </div>

            <div className="space-y-1">
              <Label htmlFor="survey-description">説明</Label>
              <Textarea
                id="survey-description"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="アンケートの目的などを入力"
                rows={2}
              />
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label>質問</Label>
                <Button variant="outline" size="sm" onClick={addQuestionRow}>
                  <Plus className="h-4 w-4 mr-1" />
                  質問を追加
                </Button>
              </div>

              {form.questions.map((question, index) => (
                <div key={index} className="border rounded-lg p-3 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">質問 {index + 1}</span>
                    {form.questions.length > 1 && (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => removeQuestionRow(index)}
                        aria-label="質問を削除"
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    )}
                  </div>

                  <Input
                    value={question.label}
                    onChange={(e) => updateQuestionRow(index, { label: e.target.value })}
                    placeholder="例: お住まいの地域"
                  />

                  <div className="grid grid-cols-2 gap-2">
                    <Select
                      value={question.type}
                      onValueChange={(value) => updateQuestionRow(index, { type: value })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="text">自由入力</SelectItem>
                        <SelectItem value="select">単一選択</SelectItem>
                        <SelectItem value="multiselect">複数選択</SelectItem>
                      </SelectContent>
                    </Select>

                    {(question.type === 'select' || question.type === 'multiselect') && (
                      <Input
                        value={question.options}
                        onChange={(e) => updateQuestionRow(index, { options: e.target.value })}
                        placeholder="選択肢 (カンマ区切り)"
                      />
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              キャンセル
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? '保存中...' : editingId ? '更新する' : '作成する'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
});

export default SurveyList;
